import { makeObservable, observable, action } from "mobx";
import axios from "axios";
import is from "is";
import { Typography } from "antd";
import TimeAgo from "react-timeago";
import { getDummyImage, jsonCopy, formatDateForLog } from "../utils/helper";
import AvatarSingle from "../components/AvatarSingle";
import store from "../stores/StoreRoot";

export class StoreTables {
  tablesList = observable([]);
  isLoaded = false;
  isWorking = false;
  isSendingData = false;
  activeTableId = null;
  activeTableData = null;
  activeTableColumns = [];
  basicApiUrl = "https://data.airrange.io/api/"; //"http://localhost:8080/api/";

  constructor(rootStore) {
    this.rootStore = rootStore;
    this.tablesList = [];
    this.lastUpdate = new Date();
    this.activeTableId = null;
    this.activeTableData = null;
    this.activeTableColumns = [];

    makeObservable(this, {
      isLoaded: observable,
      isWorking: observable,
      isSendingData: observable,
      setIsSendingData: action,
      setIsLoaded: action,
      setIsWorking: action,
      tablesList: observable,
      setTablesList: action,
      activeTableData: observable,
      activeTableId: observable,
      setActiveTableData: action,
    });
  }

  async loadTablesList(reload = false, returnVisible = false) {
    if (this.isLoaded && this.tablesList.length > 0 && !reload)
      return this.tablesList;

    const list = (await this.rootStore.appsync.getDomainTables()) ?? [];
    list.sort(function (a, b) {
      // Convert the date strings to Date objects
      let dateA = new Date(a?.modified);
      let dateB = new Date(b?.modified);
      // Subtract the dates to get a value that is either negative, positive, or zero
      return dateB - dateA;
    });
    this.setTablesList(list);
    this.setIsLoaded(true);
    const isSingleUser =
      this.rootStore.main.userDomain === this.rootStore.main.userEmail;
    if (!returnVisible || isSingleUser) return list;

    // filter the tables based on user permissions
    let filteredList = [];
    const userEmail = this.rootStore.main.userEmail;
    list.forEach((item) => {
      let bOK = item?.userId === userEmail;
      if (!bOK) {
        let collaborators = item?.data?.collaborators;
        if (collaborators && is.string(collaborators)) {
          bOK = collaborators.toLowerCase().includes(userEmail);
        }
      }
      if (bOK) filteredList.push(item);
    });
    this.rootStore.main.setLocalStoreValue("lastTablesCount", list.length);
    return filteredList;
  }

  isThisJsonAGoodTable(json) {
    if (!json) return false;
    if (!Array.isArray(json)) return false;
    if (json.length === 0) return false;
    let record = json[0];
    if (!record) return false;
    if (!is.object(record)) return false;
    return true;
  }

  async createTableItem(title, tableInfo) {
    let sourceType = tableInfo?.sourceType ?? "records";
    const isHostedTable = sourceType === "table";
    const data = jsonCopy(tableInfo?.data);
    const tableData = tableInfo?.tableData;
    const topImageUrl = getDummyImage();
    const info = {
      title: title ?? "New Table",
      sourceType: sourceType,
      data: data,
      topImageUrl: topImageUrl,
    };
    // create the record
    let newId = await this.rootStore.appsync.createDomainTable(info);
    // ===================================
    // create the hosted table
    // ===================================
    if (newId && isHostedTable) {
      const apiKey = process.env.REACT_APP_AR_TABLE_AUTH_TOKEN;
      const headers = { Authorization: `Bearer ${apiKey}` };
      const tableAuthToken = tableInfo?.data?.auth?.token ?? "";
      const data = {
        tenantId: store.main.userDomain,
        authToken: tableAuthToken,
        title: title,
      };
      const config = { headers: headers };
      await axios.put(this.basicApiUrl + "table?id=" + newId, data, config);
    }

    if (!tableInfo) return;
    if (!newId) return;

    info["id"] = newId;
    // let tableData = isHostedTable ? null : tableInfo?.data;

    if (!tableData) {
      // update the list
      await this.loadTablesList(true, true);
      return info;
    }

    // ===================================
    // create the json and upload it to AWS
    // ===================================
    let dataInfo = {};
    let uploadInfo;
    if (this.isThisJsonAGoodTable(tableData)) {
      uploadInfo = await this.rootStore.appsync.uploadHostedTableData(
        newId,
        tableData,
      );
    }

    if (uploadInfo?.size) dataInfo["size"] = uploadInfo.size;
    if (uploadInfo?.domainId) dataInfo["domainId"] = uploadInfo.domainId;
    if (uploadInfo?.fileId) dataInfo["fileId"] = uploadInfo.fileId;
    if (tableInfo?.description) info["description"] = tableInfo.description;
    if (tableInfo?.statement) dataInfo["statement"] = tableInfo.statement;
    if (tableInfo?.worksheet) dataInfo["worksheet"] = tableInfo.worksheet;

    info["data"] = dataInfo;
    // update the info
    await this.rootStore.appsync.updateDomainTable(info);
    // update the list
    await this.loadTablesList(true, true);
    return info;
  }

  async deleteTableItem(tableId) {
    if (!tableId) return;

    try {
      const apiKey = process.env.REACT_APP_AR_TABLE_AUTH_TOKEN;
      const tenantId = store.main.userDomain;
      const headers = { Authorization: `Bearer ${apiKey}` };
      const config = { headers: headers };
      await axios.delete(
        this.basicApiUrl + "table?id=" + tableId + "&tenant=" + tenantId,
        config,
      );
    } catch (error) {
      console.log("ERROR: deleteTableItem", error);
    }
  }

  async getTableItem(tableId) {
    const tableInfo = await this.rootStore.appsync.getDomainTable(tableId);
    return tableInfo;
  }

  async getTableItemData(tableItem) {
    if (!tableItem) return;
    const sourceType = tableItem.sourceType;
    const sourceUrl = tableItem?.data?.sourceUrl;
    const isSourceUrlType = is.string(sourceUrl) && sourceUrl.length > 10;
    const isHostedJsonType =
      sourceType === "automation" ||
      (tableItem?.data?.domainId && tableItem?.data?.fileId);
    let records;
    let columns;
    this.setIsWorking(true);
    if (isSourceUrlType) {
      const url = tableItem?.data?.sourceUrl;
      if (!url) return;
      const response = await axios({
        url: url,
        method: "GET",
      });
      if (response?.status !== 200) return;
      records = response.data;
    }
    if (isHostedJsonType) {
      const tableId = tableItem.id;
      if (!tableId) return;

      records = await this.rootStore.appsync.downloadHostedTableData(tableItem);
    }
    if (sourceType === "records") {
      const tableId = tableItem.id;
      if (!tableId) return;

      records = await this.rootStore.appsync.getDomainTableRecords(tableId);
    }
    if (sourceType === "table") {
      const tableId = tableItem.id;
      if (!tableId) return;

      records = []; //(await this.getHostedTableRecords(tableItem)) ?? [];
      columns = tableItem.data?.schema?.columns ?? [];
    }

    // =============================================
    // create the columns - based on the first record
    // =============================================
    if (records && records.length > 0 && !columns) {
      let record = records[0];
      columns = [];
      Object.keys(record).forEach((key) => {
        let column = {
          title: key,
          dataIndex: key,
          // ellipsis: true,
          render: (text) => (
            <div style={{ padding: 5, paddingLeft: 15 }}>
              <Typography.Paragraph ellipsis={{ rows: 2 }}>
                {text}
              </Typography.Paragraph>
            </div>
          ),
        };
        if (key === "created") {
          column.render = (text, record) => (
            <div
              style={{
                padding: 5,
                paddingLeft: 15,
                fontSize: 12,
                color: "#aaaaaa",
              }}
            >
              <TimeAgo date={record.created} />
            </div>
          );
          column.sorter = (a, b) => new Date(a.created) - new Date(b.created);
          // column.sortDirections = ["ascend", "descend"];
        }
        if (key === "userId") {
          column.render = (text, record) => (
            <div className="text-center">
              <AvatarSingle email={text} />
            </div>
          );
          column.title = "";
        }
        if (key === "app") {
          column.ellipsis = false;
        }
        if (["key", "id", "invitationId", "fileId", "appId"].includes(key)) {
          column.hidden = true;
        }
        columns.push(column);
      });
    }
    this.activeTableColumns = columns;
    this.setActiveTableData(records);
    this.setIsWorking(false);
    return records;
  }

  getSampleTableItem(type) {
    if (type === "customers") {
      return {
        sourceType: "json",
        data: {
          sourceUrl:
            "https://airrange-content.s3.eu-central-1.amazonaws.com/samples/sample-tables/sample-customers.json",
        },
        domainId: "airrange.io",
        userId: "stephan@airrange.io",
        isDeleted: false,
        id: "sample-customers",
        title: "Customer Table Sample",
      };
    }

    return {
      sourceType: "json",
      data: {
        sourceUrl:
          "https://airrange-content.s3.eu-central-1.amazonaws.com/samples/sample-tables/sample-orders.json",
      },
      domainId: "airrange.io",
      userId: "stephan@airrange.io",
      isDeleted: false,
      id: "sample-orders",
      title: "Orders Table Sample",
    };
  }

  async deleteActiveTableRecords(domainTableId) {
    const tableItem = await this.getTableItem(domainTableId);
    if (!tableItem || tableItem.sourceType !== "records") return;

    // clear records records
    let ids = [];
    this.activeTableData.forEach((element) => ids.push(element.id));
    if (ids.length === 0) return;

    // delete records from dynamo
    await this.rootStore.appsync.deleteDomainTableRecords(domainTableId, ids);
  }

  async deleteSelectedTableRecords(domainTableId, ids) {
    if (!ids || ids.length === 0) return;

    const tableItem = await this.getTableItem(domainTableId);
    if (!tableItem) return;

    if (tableItem.sourceType === "table") {
      // let changes = [];
      // ids.forEach((id) => {
      //   changes.push({ type: "delete", dataItem: { ar_id: id } });
      // });
      // let result = await this.saveHostedTableChanges(tableItem, changes);
    } else if (tableItem.sourceType === "records") {
      return await this.rootStore.appsync.deleteDomainTableRecords(
        domainTableId,
        ids,
      );
    }
  }

  clearActiveTableData() {
    this.activeTableId = null;
    this.activeTableData = null;
    this.activeTableColumns = [];
  }

  setActiveTableData(data) {
    this.activeTableData = data;
  }

  // =============================================
  // HOSTED TABLES
  // =============================================

  async updateHostedTableSchema(tableItem, simpleSchema) {
    if (!tableItem || !simpleSchema) return;

    const apiKey = process.env.REACT_APP_AR_TABLE_AUTH_TOKEN;
    const headers = { Authorization: `Bearer ${apiKey}` };
    const tableAuthToken = tableItem?.data?.auth?.token ?? "";
    const data = {
      tenantId: store.main.userDomain,
      authToken: tableAuthToken,
      schema: simpleSchema,
    };
    const config = { headers: headers };
    await axios.post(
      this.basicApiUrl + "table?id=" + tableItem.id,
      data,
      config,
    );
  }

  async getHostedTableRecords(tableItem) {
    const tableId = tableItem.id;
    const token = tableItem?.data?.auth?.token;
    let records = [];
    // update the changes
    const getDataUrl =
      this.basicApiUrl + "table?id=" + tableId + "&token=" + token;
    try {
      const data = await axios.get(getDataUrl);
      records = data?.data ?? [];
    } catch (error) {
      console.log("ERROR: saveHostedTableChanges", error);
    }
    return records;
  }

  // isTable(id) {
  //   const foundIndex = this.tablesList.findIndex(
  //     (tableItem) => tableItem.id === id
  //   );
  //   return foundIndex !== -1;
  // }

  async exportTableData(tableItem, getRecordsProc) {
    let records = this.activeTableData ?? [];
    let columns = this.activeTableColumns;

    // get live records if necessary
    if (!records || records.length === 0) {
      if (tableItem.sourceType === "table") {
        if (getRecordsProc) records = (await getRecordsProc()) ?? [];
        if (!records || records.length === 0)
          records = (await this.getHostedTableRecords(tableItem)) ?? [];
      }
    }

    try {
      const ExcelIO = await import("@mescius/spread-excelio");
      ExcelIO["LicenseKey"] = process.env.REACT_APP_SPREADJS18_KEY;
      const GC = await import("@mescius/spread-sheets");

      const excelIO = new ExcelIO.IO();
      const book = new GC.Spread.Sheets.Workbook();
      const sheet = book.getActiveSheet();

      // write the header
      if (!is.array(columns)) {
        let test = Object.values(columns);
        if (is.array(test)) columns = test;
      }
      columns.forEach((col, index) => {
        let title = col.title ?? col.caption ?? col.name;
        sheet.setValue(0, index, title);
      });
      // process the rows
      records.forEach((element, rowIndex) => {
        // get the column values
        columns.forEach((col, colIndex) => {
          let title = col.name ?? col.title ?? col.caption;
          let value = element[title];
          // format the date
          if (title === "created") {
            value = new Date(value).toLocaleString();
          }
          sheet.setValue(rowIndex + 1, colIndex, value);
        });
      });
      // auto fit
      columns.forEach((element, index) => {
        sheet.autoFitColumn(index);
      });

      const fileTitle = tableItem?.title ?? "Table";

      excelIO.save(
        book,
        (blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = fileTitle + "-" + formatDateForLog() + ".xlsx";
          a.click();
        },
        (error) => {
          console.log(error);
        },
      );
    } catch (error) {
      console.log("ERROR EXPORTING TABLE DATA", error);
    }
  }

  setIsLoaded(loaded) {
    this.isLoaded = loaded;
  }

  setIsWorking(value) {
    this.isWorking = value;
  }

  setIsSendingData(value) {
    this.isSendingData = value;
  }

  async setTablesList(tablesInfo) {
    this.tablesList = tablesInfo ?? [];
    this.setIsLoaded(true);
  }

  setUpdatedTablesList(tablesInfo) {
    this.tablesList = tablesInfo ?? [];
    this.lastUpdate = new Date();
  }
}
